'use client';

/**
 * Add Product Form Widget
 *
 * Two modes: Catalog (Odoo) and Custom/Bespoke (Neo4j virtual product).
 *
 * Catalog mode: Full product creation via Odoo action gateway.
 *   Panel 1: Identity & Commercial (name, SKU, prices, vendor, collection, brand).
 *   Panel 2: Classification & Description (category, type, descriptions).
 *   SKU dedup: offers Edit Form / Create Anyway / Update Instead.
 *
 * Custom mode: Buyer product wish — relaxed fields, saved to Neo4j only.
 *   Name + Project required. Category, description, dimensions, materials,
 *   price range, quantity all optional.
 *
 * @layer presentation
 * @arch_component_type canvas_widget
 * @arch_domain discovery
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Package,
  ArrowLeft,
  Plus,
  CheckCircle,
  AlertCircle,
  Loader2,
  DollarSign,
  Tag,
  FileText,
  ChevronUp,
  ChevronDown,
  Building2,
  Layers,
  FolderOpen,
  Ruler,
  Paintbrush,
  Hash,
  Image as ImageIcon,
  X,
} from 'lucide-react';
import { api } from '@/lib/api/client';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import type { DiscoveryActionResult } from '@/lib/api/types';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { processImageForObserve, isValidImageFile } from '@/lib/utils/image-processing';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils/cn';
import { toast } from 'sonner';

// ============================================================
// Constants
// ============================================================

const MAX_CUSTOM_IMAGES = 3;

const CATEGORIES = [
  { id: 'seating', label: 'Seating' },
  { id: 'table', label: 'Table' },
  { id: 'joinery', label: 'Joinery' },
  { id: 'outdoor', label: 'Outdoor' },
] as const;

interface StagedImage {
  id: string;
  base64: string;
  thumbnail: string;
  filename: string;
  sizeKB: number;
}

const PRODUCT_TYPES = [
  { id: 'consu', label: 'Goods' },
  { id: 'service', label: 'Service' },
  { id: 'combo', label: 'Combo' },
] as const;

// ============================================================
// Types
// ============================================================

interface ProductFormData {
  name: string;
  default_code: string;
  list_price: string;
  standard_price: string;
  vendor_id: number | null;
  collection: string;
  brand: string;
  category: string;
  type: string;
  description: string;
  description_sale: string;
}

interface CustomProductFormData {
  name: string;
  project_sid: string;
  project_name: string;
  category: string;
  description: string;
  dimensions: string;
  materials: string;
  indicative_price_min: string;
  indicative_price_max: string;
  quantity: string;
}

interface VendorOption {
  name: string;
  odoo_id: number;
}

interface ProjectOption {
  name: string;
  semantic_id: string;
}

interface AddProductFormWidgetProps {
  onBack?: () => void;
  entityId?: string;
  title?: string;
}

const INITIAL_FORM: ProductFormData = {
  name: '',
  default_code: '',
  list_price: '',
  standard_price: '',
  vendor_id: null,
  collection: '',
  brand: '',
  category: '',
  type: '',
  description: '',
  description_sale: '',
};

const INITIAL_CUSTOM: CustomProductFormData = {
  name: '',
  project_sid: '',
  project_name: '',
  category: '',
  description: '',
  dimensions: '',
  materials: '',
  indicative_price_min: '',
  indicative_price_max: '',
  quantity: '',
};

const CATALOG_REQUIRED = 11;
const CUSTOM_REQUIRED = 2; // name + project

function countCatalogCompleted(form: ProductFormData): number {
  let count = 0;
  if (form.name.trim()) count++;
  if (form.default_code.trim()) count++;
  if (form.list_price.trim() && parseFloat(form.list_price) > 0) count++;
  if (form.standard_price.trim() && parseFloat(form.standard_price) >= 0) count++;
  if (form.vendor_id !== null && form.vendor_id > 0) count++;
  if (form.collection.trim()) count++;
  if (form.brand.trim()) count++;
  if (form.category) count++;
  if (form.type) count++;
  if (form.description.trim()) count++;
  if (form.description_sale.trim()) count++;
  return count;
}

function countCustomCompleted(form: CustomProductFormData): number {
  let count = 0;
  if (form.name.trim()) count++;
  if (form.project_sid) count++;
  return count;
}

/** Build the submit payload from catalog form state. */
function buildPayload(form: ProductFormData) {
  return {
    name: form.name,
    default_code: form.default_code,
    list_price: parseFloat(form.list_price),
    standard_price: parseFloat(form.standard_price),
    vendor_id: form.vendor_id,
    collection: form.collection,
    brand: form.brand,
    category: form.category,
    type: form.type,
    description: form.description,
    description_sale: form.description_sale,
  };
}

// ============================================================
// FieldRow — icon + label layout (matches AddVendorFormWidget)
// ============================================================

function FieldRow({
  icon: Icon,
  label,
  required,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs font-bold text-foreground">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        {label}
        {required && <span className="text-destructive">*</span>}
      </label>
      {children}
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

export function AddProductFormWidget({ onBack }: AddProductFormWidgetProps) {
  const createStrategy = useCapabilityStore((s) => s.strategyName('discovery', 'create'));
  const updateStrategy = useCapabilityStore((s) => s.strategyName('discovery', 'update'));
  const createWishStrategy = useCapabilityStore((s) => s.strategyName('discovery', 'create_wish'));

  // -- Mode --
  const [mode, setMode] = useState<'catalog' | 'custom'>('catalog');

  // -- Catalog form state --
  const [form, setForm] = useState<ProductFormData>({ ...INITIAL_FORM });
  const [detailedExpanded, setDetailedExpanded] = useState(false);

  // -- Custom form state --
  const [customForm, setCustomForm] = useState<CustomProductFormData>({ ...INITIAL_CUSTOM });
  const [customImages, setCustomImages] = useState<StagedImage[]>([]);
  const [imageProcessing, setImageProcessing] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // -- Shared state --
  const [errors, setErrors] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState('');
  const [submittedRef, setSubmittedRef] = useState<{ record_id?: number; reference?: string; semantic_id?: string } | null>(null);
  const [submittedAction, setSubmittedAction] = useState<'created' | 'updated'>('created');
  const [submittedMode, setSubmittedMode] = useState<'catalog' | 'custom'>('catalog');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [dedupResult, setDedupResult] = useState<DiscoveryActionResult | null>(null);

  // -- Progress --
  const totalRequired = mode === 'catalog' ? CATALOG_REQUIRED : CUSTOM_REQUIRED;
  const completed = mode === 'catalog' ? countCatalogCompleted(form) : countCustomCompleted(customForm);
  const progressPct = Math.round((completed / totalRequired) * 100);
  const allRequired = completed === totalRequired;

  // -- Load vendor dropdown from Neo4j (catalog mode) --

  useEffect(() => {
    let cancelled = false;
    async function loadVendors() {
      setVendorsLoading(true);
      try {
        const res = await api.getStrategyData<any>('entity_list', {
          entity_type: 'Actor',
          limit: 200,
        });
        if (cancelled) return;
        const items = res?.data?.items || res?.data || [];
        const vendorList: VendorOption[] = (Array.isArray(items) ? items : [])
          .filter((a: any) => a.actor_type === 'vendor' && a.odoo_id)
          .map((a: any) => ({ name: a.name || a.display_name || '', odoo_id: a.odoo_id }))
          .sort((a: VendorOption, b: VendorOption) => a.name.localeCompare(b.name));
        setVendors(vendorList);
      } catch {
        // Vendor list is non-critical — user can still type ID
      } finally {
        if (!cancelled) setVendorsLoading(false);
      }
    }
    loadVendors();
    return () => { cancelled = true; };
  }, []);

  // -- Load project dropdown (only when custom mode) --

  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'custom') return;
    if (projects.length > 0) return; // already loaded
    let cancelled = false;
    async function loadProjects() {
      setProjectsLoading(true);
      setProjectsError(null);
      try {
        const strategyRes = await api.getStrategyData<any>('obeya_projects', { limit: 50 });
        if (cancelled) return;
        if (!strategyRes.success) throw new Error(strategyRes.error || 'obeya_projects failed');
        const res = strategyRes.data;
        const raw = res?.projects || res?.data?.items || res?.items || [];
        const list: ProjectOption[] = (Array.isArray(raw) ? raw : [])
          .filter((p: any) => p.semantic_id && p.name)
          .map((p: any) => ({ name: p.name, semantic_id: p.semantic_id }))
          .sort((a: ProjectOption, b: ProjectOption) => a.name.localeCompare(b.name));
        if (list.length === 0) {
          setProjectsError('No projects found — create a project first');
        }
        setProjects(list);
      } catch (err: any) {
        if (!cancelled) {
          const msg = err?.message || 'Unknown error';
          setProjectsError(`Failed to load projects: ${msg}`);
          console.error('Project list load failed:', err);
        }
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    }
    loadProjects();
    return () => { cancelled = true; };
  }, [mode]);

  // -- Field updaters --

  const set = useCallback(
    <K extends keyof ProductFormData>(key: K, value: ProductFormData[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      setErrors([]);
    },
    [],
  );

  const setCustom = useCallback(
    <K extends keyof CustomProductFormData>(key: K, value: CustomProductFormData[K]) => {
      setCustomForm((prev) => ({ ...prev, [key]: value }));
      setErrors([]);
    },
    [],
  );

  // -- Image management (custom mode) --

  const addCustomImages = useCallback(async (files: File[]) => {
    const available = MAX_CUSTOM_IMAGES - customImages.length;
    if (available <= 0) {
      toast.warning(`Maximum ${MAX_CUSTOM_IMAGES} images allowed`);
      return;
    }
    const valid = files.filter(isValidImageFile);
    if (valid.length < files.length) {
      toast.warning(`${files.length - valid.length} file(s) skipped — only images allowed`);
    }
    const batch = valid.slice(0, available);
    if (batch.length === 0) return;

    setImageProcessing(true);
    for (const file of batch) {
      try {
        const { base64, sizeKB } = await processImageForObserve(file);
        setCustomImages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            base64,
            thumbnail: URL.createObjectURL(file),
            filename: file.name,
            sizeKB,
          },
        ]);
      } catch (err) {
        console.error('Failed to process image:', file.name, err);
        toast.error(`Failed to process ${file.name}`);
      }
    }
    setImageProcessing(false);
  }, [customImages.length]);

  const removeCustomImage = useCallback((id: string) => {
    setCustomImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img) URL.revokeObjectURL(img.thumbnail);
      return prev.filter((i) => i.id !== id);
    });
  }, []);

  // -- Validation (catalog) --

  const validate = useCallback((): string[] => {
    const errs: string[] = [];
    if (!form.name.trim()) errs.push('Product name is required');
    if (!form.default_code.trim()) errs.push('SKU is required');
    const price = parseFloat(form.list_price);
    if (!form.list_price.trim() || isNaN(price)) errs.push('Sale price is required');
    const cost = parseFloat(form.standard_price);
    if (!form.standard_price.trim() || isNaN(cost)) errs.push('Cost price is required');
    if (!form.vendor_id || form.vendor_id <= 0) errs.push('Vendor is required');
    if (!form.collection.trim()) errs.push('Collection is required');
    if (!form.brand.trim()) errs.push('Brand is required');
    if (!form.category) errs.push('Category is required');
    if (!form.type) errs.push('Product type is required');
    if (!form.description.trim()) errs.push('Description is required');
    if (!form.description_sale.trim()) errs.push('Sales description is required');
    return errs;
  }, [form]);

  // -- Validation (custom) --

  const validateCustom = useCallback((): string[] => {
    const errs: string[] = [];
    if (!customForm.name.trim()) errs.push('Product name is required');
    if (!customForm.project_sid) errs.push('Project is required');
    return errs;
  }, [customForm]);

  // -- Submit catalog via BFF --

  const handleSubmit = useCallback(async () => {
    const errs = validate();
    if (errs.length > 0) { setErrors(errs); return; }

    setIsSubmitting(true);
    setErrors([]);
    setDedupResult(null);

    try {
      const res = await api.getStrategyData<any>(createStrategy!, {
        ...buildPayload(form),
        force_create: false,
      });

      if (!res.success) {
        setErrors([res.error || 'Failed to create product']);
        return;
      }

      // Dedup hit — show match info, let user decide
      if (res.data.dedup_hit) {
        setDedupResult(res.data as DiscoveryActionResult);
        return;
      }

      setSubmittedName(form.name.trim());
      setSubmittedRef({ record_id: res.data.record_id, reference: res.data.reference });
      setSubmittedAction('created');
      setSubmittedMode('catalog');
      setSubmitted(true);
      toast.success(`Product "${form.name.trim()}" created`);
    } catch (err: any) {
      setErrors([err.message || 'Network error']);
    } finally {
      setIsSubmitting(false);
    }
  }, [form, validate]);

  // -- Force create (bypass dedup) --

  const handleForceCreate = useCallback(async () => {
    setIsSubmitting(true);
    setDedupResult(null);
    try {
      const res = await api.getStrategyData<any>(createStrategy!, {
        ...buildPayload(form),
        force_create: true,
      });
      if (!res.success) { setErrors([res.error || 'Failed to create product']); return; }
      setSubmittedName(form.name.trim());
      setSubmittedRef({ record_id: res.data.record_id, reference: res.data.reference });
      setSubmittedAction('created');
      setSubmittedMode('catalog');
      setSubmitted(true);
      toast.success(`Product "${form.name.trim()}" created`);
    } catch (err: any) {
      setErrors([err.message || 'Network error']);
    } finally {
      setIsSubmitting(false);
    }
  }, [form]);

  // -- Update existing product instead --

  const handleUpdateInstead = useCallback(async () => {
    if (!dedupResult?.product) return;
    setIsSubmitting(true);
    setDedupResult(null);
    try {
      const res = await api.getStrategyData<any>(updateStrategy!, {
        record_id: dedupResult.product.odoo_id,
        ...buildPayload(form),
      });
      if (!res.success) { setErrors([res.error || 'Failed to update product']); return; }
      setSubmittedName(form.name.trim());
      setSubmittedRef({ record_id: res.data.record_id, reference: res.data.reference });
      setSubmittedAction('updated');
      setSubmittedMode('catalog');
      setSubmitted(true);
      toast.success(`Product "${form.name.trim()}" updated`);
    } catch (err: any) {
      setErrors([err.message || 'Network error']);
    } finally {
      setIsSubmitting(false);
    }
  }, [form, dedupResult]);

  // -- Submit custom (buyer wish) --

  const handleCustomSubmit = useCallback(async () => {
    const errs = validateCustom();
    if (errs.length > 0) { setErrors(errs); return; }

    setIsSubmitting(true);
    setErrors([]);

    try {
      const res = await api.getStrategyData<any>(createWishStrategy!, {
        name: customForm.name.trim(),
        project_sid: customForm.project_sid,
        project_name: customForm.project_name,
        category: customForm.category,
        description: customForm.description,
        dimensions: customForm.dimensions,
        materials: customForm.materials,
        indicative_price_min: customForm.indicative_price_min
          ? parseFloat(customForm.indicative_price_min)
          : null,
        indicative_price_max: customForm.indicative_price_max
          ? parseFloat(customForm.indicative_price_max)
          : null,
        quantity: customForm.quantity ? parseInt(customForm.quantity) : null,
        images: customImages.map((img) => img.base64),
      });

      if (!res.success) {
        setErrors([res.error || 'Failed to save product wish']);
        return;
      }

      setSubmittedName(customForm.name.trim());
      setSubmittedRef({ semantic_id: res.data.semantic_id });
      setSubmittedAction('created');
      setSubmittedMode('custom');
      setSubmitted(true);
      toast.success(`Product wish "${customForm.name.trim()}" saved`);
    } catch (err: any) {
      setErrors([err.message || 'Network error']);
    } finally {
      setIsSubmitting(false);
    }
  }, [customForm, validateCustom]);

  // -- Reset --

  const handleReset = useCallback(() => {
    setForm({ ...INITIAL_FORM });
    setCustomForm({ ...INITIAL_CUSTOM });
    customImages.forEach((img) => URL.revokeObjectURL(img.thumbnail));
    setCustomImages([]);
    setErrors([]);
    setSubmitted(false);
    setSubmittedName('');
    setSubmittedRef(null);
    setSubmittedAction('created');
    setSubmittedMode('catalog');
    setDetailedExpanded(false);
    setDedupResult(null);
  }, [customImages]);

  // ============================================================
  // Success State
  // ============================================================

  if (submitted) {
    const isCustom = submittedMode === 'custom';
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 p-8">
        <div className="w-16 h-16 rounded-2xl bg-success/10 border border-success/20 flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-success" />
        </div>
        <h2 className="text-lg font-semibold">{submittedName} {submittedAction}!</h2>
        <p className="text-sm text-muted-foreground">
          {isCustom
            ? 'Product wish saved to project intelligence.'
            : `Product has been ${submittedAction} in Odoo.`}
        </p>
        {submittedRef && (
          <p className="text-xs text-muted-foreground font-mono">
            {submittedRef.reference && <span>SKU: {submittedRef.reference}</span>}
            {submittedRef.reference && submittedRef.record_id && <span> · </span>}
            {submittedRef.record_id && <span>Odoo ID: {submittedRef.record_id}</span>}
            {submittedRef.semantic_id && <span>ID: {submittedRef.semantic_id}</span>}
          </p>
        )}
        <div className="flex gap-3 mt-4">
          <Button variant="outline" size="sm" onClick={onBack}>
            Done
          </Button>
          <Button size="sm" onClick={handleReset}>
            <Plus className="w-4 h-4 mr-1.5" />
            Add Another Product
          </Button>
        </div>
      </div>
    );
  }

  // ============================================================
  // Form
  // ============================================================

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3 border-b border-border bg-background/95 backdrop-blur-sm flex-shrink-0">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Discovery</span>
        </button>
        <div className="h-4 w-px bg-border" />
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold">Add Product</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
            Required fields
          </span>
          <span className="text-[10px] text-muted-foreground font-medium">
            {completed}/{totalRequired}
          </span>
          <div className="w-20 h-1 bg-muted rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500 ease-out',
                allRequired ? 'bg-success' : 'bg-primary',
              )}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Scrollable form body */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-4">
        <div className="max-w-2xl mx-auto space-y-6">

          {/* ── Mode Toggle ── */}
          <div className="flex gap-1 p-1 bg-muted rounded-lg">
            <button
              onClick={() => { if (mode !== 'catalog') { setMode('catalog'); setErrors([]); setDedupResult(null); } }}
              className={cn(
                'flex-1 text-xs font-medium py-1.5 rounded-md transition-colors',
                mode === 'catalog'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Catalog Product
            </button>
            <button
              onClick={() => { if (mode !== 'custom') { setMode('custom'); setErrors([]); setDedupResult(null); } }}
              className={cn(
                'flex-1 text-xs font-medium py-1.5 rounded-md transition-colors',
                mode === 'custom'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Custom / Bespoke
            </button>
          </div>

          {/* ============================================ */}
          {/* CATALOG MODE                                 */}
          {/* ============================================ */}

          {mode === 'catalog' && (
            <>
              {/* ── Panel 1: Identity & Commercial ── */}
              <div className="rounded-xl border border-border overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 bg-card border-b border-border">
                  <div className="w-5 h-5 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[9px] font-bold text-primary">1</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider">Identity & Commercial</h3>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Name, SKU, pricing, vendor, collection, brand</p>
                  </div>
                  {countCatalogCompleted(form) >= 7 && (
                    <div className="flex items-center gap-1.5 text-[11px] text-success bg-success/10 border border-success/20 px-2.5 py-1 rounded-full">
                      <CheckCircle className="w-3 h-3" />
                      Complete
                    </div>
                  )}
                </div>
                <div className="p-5 space-y-4 bg-card">
                  {/* Name + SKU */}
                  <div className="grid grid-cols-2 gap-3">
                    <FieldRow icon={Package} label="Product Name" required>
                      <Input
                        placeholder="e.g. Carmody Lounge Chair"
                        value={form.name}
                        onChange={(e) => set('name', e.target.value)}
                        className="h-9"
                      />
                    </FieldRow>
                    <FieldRow icon={Tag} label="SKU / Internal Reference" required>
                      <Input
                        placeholder="e.g. CARM-LC-001"
                        value={form.default_code}
                        onChange={(e) => set('default_code', e.target.value)}
                        className="h-9 font-mono"
                      />
                    </FieldRow>
                  </div>

                  {/* Prices */}
                  <div className="grid grid-cols-2 gap-3">
                    <FieldRow icon={DollarSign} label="Sale Price" required>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        placeholder="0.00"
                        value={form.list_price}
                        onChange={(e) => set('list_price', e.target.value)}
                        className="h-9"
                      />
                    </FieldRow>
                    <FieldRow icon={DollarSign} label="Cost Price" required>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        placeholder="0.00"
                        value={form.standard_price}
                        onChange={(e) => set('standard_price', e.target.value)}
                        className="h-9"
                      />
                    </FieldRow>
                  </div>

                  {/* Vendor */}
                  <FieldRow icon={Building2} label="Vendor" required>
                    {vendorsLoading ? (
                      <div className="flex items-center gap-2 h-9 text-xs text-muted-foreground">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Loading vendors...
                      </div>
                    ) : vendors.length > 0 ? (
                      <Select
                        value={form.vendor_id ? String(form.vendor_id) : ''}
                        onValueChange={(v) => set('vendor_id', parseInt(v))}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select vendor..." />
                        </SelectTrigger>
                        <SelectContent>
                          {vendors.map((v) => (
                            <SelectItem key={v.odoo_id} value={String(v.odoo_id)}>
                              {v.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type="number"
                        min={1}
                        placeholder="Odoo vendor ID"
                        value={form.vendor_id ?? ''}
                        onChange={(e) => set('vendor_id', parseInt(e.target.value) || null)}
                        className="h-9"
                      />
                    )}
                    <p className="text-[10px] text-muted-foreground">
                      Vendor not listed? Register them first from the Vendor Portal.
                    </p>
                  </FieldRow>

                  {/* Collection + Brand */}
                  <div className="grid grid-cols-2 gap-3">
                    <FieldRow icon={Layers} label="Collection" required>
                      <Input
                        placeholder="e.g. Studio Series"
                        value={form.collection}
                        onChange={(e) => set('collection', e.target.value)}
                        className="h-9"
                      />
                    </FieldRow>
                    <FieldRow icon={Tag} label="Brand" required>
                      <Input
                        placeholder="e.g. Carmody Studio"
                        value={form.brand}
                        onChange={(e) => set('brand', e.target.value)}
                        className="h-9"
                      />
                    </FieldRow>
                  </div>
                </div>
              </div>

              {/* ── Panel 2: Classification & Description (collapsible) ── */}
              <div className="rounded-xl border border-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDetailedExpanded((p) => !p)}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-card border-b border-border hover:bg-muted transition-colors"
                >
                  <div className="w-5 h-5 rounded-full bg-info/10 border border-info/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[9px] font-bold text-info">2</span>
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider">Classification & Description</h3>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Category, product type, descriptions</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {detailedExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </div>
                </button>

                {detailedExpanded && (
                  <div className="p-5 space-y-4 bg-card">
                    {/* Category + Type */}
                    <div className="grid grid-cols-2 gap-3">
                      <FieldRow icon={Layers} label="Category" required>
                        <Select value={form.category} onValueChange={(v) => set('category', v)}>
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select category..." />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FieldRow>
                      <FieldRow icon={Package} label="Product Type" required>
                        <Select value={form.type} onValueChange={(v) => set('type', v)}>
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select type..." />
                          </SelectTrigger>
                          <SelectContent>
                            {PRODUCT_TYPES.map((t) => (
                              <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FieldRow>
                    </div>

                    <div className="border-t border-border" />

                    {/* Description */}
                    <FieldRow icon={FileText} label="Internal Description" required>
                      <Textarea
                        value={form.description}
                        onChange={(e) => set('description', e.target.value)}
                        placeholder="Internal notes — materials, construction, special requirements..."
                        className="min-h-[80px]"
                      />
                    </FieldRow>

                    {/* Sales Description */}
                    <FieldRow icon={FileText} label="Sales Description" required>
                      <Textarea
                        value={form.description_sale}
                        onChange={(e) => set('description_sale', e.target.value)}
                        placeholder="Customer-facing description — what appears on quotes and invoices..."
                        className="min-h-[80px]"
                      />
                    </FieldRow>
                  </div>
                )}
              </div>

              {/* Dedup Warning */}
              {dedupResult && dedupResult.product && (
                <div className="rounded-lg border border-warning/20 bg-warning/5 p-4 space-y-3">
                  <div className="flex items-center gap-2 text-warning font-medium">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">Product with this SKU already exists</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <strong className="text-foreground">{dedupResult.product.name}</strong>
                    {' '}(Odoo ID: {dedupResult.product.odoo_id})
                    {' '}&mdash; {dedupResult.product.match_reasons.join(', ')}
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setDedupResult(null)}>
                      Edit Form
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleForceCreate} disabled={isSubmitting}>
                      Create Anyway
                    </Button>
                    <Button size="sm" variant="default" onClick={handleUpdateInstead} disabled={isSubmitting}>
                      Update Instead
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ============================================ */}
          {/* CUSTOM MODE                                  */}
          {/* ============================================ */}

          {mode === 'custom' && (
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 bg-card border-b border-border">
                <div className="w-5 h-5 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                  <Package className="w-3 h-3 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider">Product Wish</h3>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Describe what you need — vendors will quote against this</p>
                </div>
              </div>
              <div className="p-5 space-y-4 bg-card">
                {/* Name */}
                <FieldRow icon={Package} label="Product Name" required>
                  <Input
                    placeholder="e.g. Custom Reception Desk"
                    value={customForm.name}
                    onChange={(e) => setCustom('name', e.target.value)}
                    className="h-9"
                  />
                </FieldRow>

                {/* Project */}
                <FieldRow icon={FolderOpen} label="Project" required>
                  {projectsLoading ? (
                    <div className="flex items-center gap-2 h-9 text-xs text-muted-foreground">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Loading projects...
                    </div>
                  ) : projectsError ? (
                    <div className="flex items-center gap-2 h-9 text-xs text-destructive">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      {projectsError}
                    </div>
                  ) : (
                    <Select
                      value={customForm.project_sid}
                      onValueChange={(sid) => {
                        const proj = projects.find((p) => p.semantic_id === sid);
                        setCustom('project_sid', sid);
                        setCustom('project_name', proj?.name || '');
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select project..." />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((p) => (
                          <SelectItem key={p.semantic_id} value={p.semantic_id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </FieldRow>

                {/* Category */}
                <FieldRow icon={Layers} label="Category">
                  <Select value={customForm.category} onValueChange={(v) => setCustom('category', v)}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select category..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldRow>

                {/* Description */}
                <FieldRow icon={FileText} label="Description">
                  <Textarea
                    value={customForm.description}
                    onChange={(e) => setCustom('description', e.target.value)}
                    placeholder="Concept, requirements, special notes..."
                    className="min-h-[80px]"
                  />
                </FieldRow>

                {/* Reference Images */}
                <FieldRow icon={ImageIcon} label={`Reference Images (${customImages.length}/${MAX_CUSTOM_IMAGES})`}>
                  <div className="flex items-center gap-2">
                    {customImages.map((img) => (
                      <div key={img.id} className="relative group w-16 h-16">
                        <img
                          src={img.thumbnail}
                          alt={img.filename}
                          className="w-full h-full object-cover rounded border border-border"
                        />
                        <button
                          type="button"
                          onClick={() => removeCustomImage(img.id)}
                          className={cn(
                            'absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full',
                            'bg-destructive text-destructive-foreground',
                            'flex items-center justify-center',
                            'opacity-0 group-hover:opacity-100 transition-opacity',
                          )}
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                        <span className="absolute bottom-0 right-0 text-[8px] bg-background/80 px-0.5 rounded-tl">
                          {img.sizeKB}KB
                        </span>
                      </div>
                    ))}
                    {customImages.length < MAX_CUSTOM_IMAGES && (
                      <button
                        type="button"
                        onClick={() => imageInputRef.current?.click()}
                        disabled={imageProcessing}
                        className={cn(
                          'w-16 h-16 border-2 border-dashed border-muted-foreground/30 rounded',
                          'flex items-center justify-center',
                          'hover:border-primary hover:bg-accent/50 transition-colors',
                          'disabled:opacity-50 disabled:cursor-not-allowed',
                        )}
                      >
                        {imageProcessing ? (
                          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                        ) : (
                          <Plus className="w-4 h-4 text-muted-foreground" />
                        )}
                      </button>
                    )}
                  </div>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      addCustomImages(files);
                      if (imageInputRef.current) imageInputRef.current.value = '';
                    }}
                    className="hidden"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Reference photos, mood images, or sketches. Normalized to 512x512 for search.
                  </p>
                </FieldRow>

                <div className="border-t border-border" />

                {/* Dimensions + Materials */}
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow icon={Ruler} label="Dimensions (approx)">
                    <Input
                      placeholder="e.g. 2400mm x 600mm x 750mm"
                      value={customForm.dimensions}
                      onChange={(e) => setCustom('dimensions', e.target.value)}
                      className="h-9"
                    />
                  </FieldRow>
                  <FieldRow icon={Paintbrush} label="Materials / Finishes">
                    <Input
                      placeholder="e.g. American Oak, COM fabric"
                      value={customForm.materials}
                      onChange={(e) => setCustom('materials', e.target.value)}
                      className="h-9"
                    />
                  </FieldRow>
                </div>

                {/* Price Range + Quantity */}
                <div className="grid grid-cols-3 gap-3">
                  <FieldRow icon={DollarSign} label="Price Min">
                    <Input
                      type="number"
                      min={0}
                      step={0.01}
                      placeholder="0.00"
                      value={customForm.indicative_price_min}
                      onChange={(e) => setCustom('indicative_price_min', e.target.value)}
                      className="h-9"
                    />
                  </FieldRow>
                  <FieldRow icon={DollarSign} label="Price Max">
                    <Input
                      type="number"
                      min={0}
                      step={0.01}
                      placeholder="0.00"
                      value={customForm.indicative_price_max}
                      onChange={(e) => setCustom('indicative_price_max', e.target.value)}
                      className="h-9"
                    />
                  </FieldRow>
                  <FieldRow icon={Hash} label="Quantity">
                    <Input
                      type="number"
                      min={1}
                      step={1}
                      placeholder="1"
                      value={customForm.quantity}
                      onChange={(e) => setCustom('quantity', e.target.value)}
                      className="h-9"
                    />
                  </FieldRow>
                </div>
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {errors.length > 0 && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-4 h-4 text-destructive" />
                <span className="text-sm font-medium text-destructive">Please fix the following:</span>
              </div>
              <ul className="list-disc list-inside text-xs text-destructive space-y-0.5">
                {errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-between gap-3 pb-4">
            <Button variant="ghost" size="sm" onClick={onBack} className="text-muted-foreground hover:text-foreground">
              Cancel
            </Button>
            {mode === 'catalog' ? (
              <Button size="sm" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating...</>
                ) : (
                  <><Package className="w-3.5 h-3.5 mr-1" /> Create Product</>
                )}
              </Button>
            ) : (
              <Button size="sm" onClick={handleCustomSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                ) : (
                  <><Package className="w-3.5 h-3.5 mr-1" /> Save Product Wish</>
                )}
              </Button>
            )}
          </div>

          {/* Footer note */}
          <div className="text-[10px] text-muted-foreground/60 text-center pt-1 pb-2">
            {mode === 'catalog'
              ? 'Product will be created in Odoo and synced to the digital twin'
              : 'Product wish will be saved to project intelligence (Neo4j). No Odoo record until a vendor quotes it.'}
          </div>
        </div>
      </div>
    </div>
  );
}
